<template>
  <Template.Define>
    <span v-if="pending || icon" class="relative shrink-0">
      <ArrowPathIcon v-if="pending" class="size-4 animate-spin" />
      <component :is="iconMappingMicro[icon]" v-else-if="icon" class="size-4" />

      <span
        v-if="badge"
        class="absolute -right-0.5 -top-0.5 size-1.5 rounded-full bg-yellow-500 dark:bg-yellow-400"
      />
    </span>

    <span :class="[icon && 'hidden sm:block', 'whitespace-nowrap']">
      {{ label + (href ? " ↗" : "") }}
    </span>
  </Template.Define>

  <Tooltip.Hint :disabled="isDesktop || !icon" :text="label">
    <a
      v-if="href && !disabled"
      v-bind="$attrs"
      :aria-label="label"
      :aria-description="ariaDescription"
      :href="href"
      target="_blank"
      :data-emphasis-identifier="id || emphasisId"
      :class="cls"
    >
      <Template.Use />
    </a>
    <RouterLink
      v-else-if="to && !disabled"
      v-bind="$attrs"
      :aria-label="label"
      :aria-description="ariaDescription"
      :to="to"
      :data-emphasis-identifier="id || emphasisId"
      :class="cls"
    >
      <Template.Use />
    </RouterLink>
    <button
      v-else
      v-bind="$attrs"
      ref="trigger"
      type="button"
      :disabled="disabled"
      :aria-label="label"
      :aria-description="ariaDescription"
      :data-emphasis-identifier="id || emphasisId"
      :class="cls"
    >
      <Template.Use />
    </button>
  </Tooltip.Hint>
</template>

<script lang="ts" setup>
import { computed, normalizeClass, useTemplateRef } from "vue";
import { useRouter } from "vue-router";

import ArrowPathIcon from "@/icons/heroicons/arrow-path-micro.svg";
import {
  type MappedIconMicro,
  iconMappingMicro,
} from "@/icons/icon-mapping-micro";
import { useMediaQuery } from "@/lib/composables/media-query";
import { createReusableTemplate } from "@/lib/composables/template";
import { formatKeybind, useKeybinds } from "@/lib/hotkeys";
import type { RouteLocation } from "@/router/types";

import Tooltip from "../Tooltip";

type Variant = "primary" | "secondary" | "negative";

const router = useRouter();

const Template = createReusableTemplate();

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(
  defineProps<{
    to?: RouteLocation;
    href?: string;
    label: string;
    description?: string;
    hotkey?: string;
    icon?: MappedIconMicro;
    variant?: Variant;
    disabled?: boolean;
    pending?: boolean;
    active?: boolean;
    badge?: boolean;
    id?: string;
  }>(),
  {
    variant: "secondary",
  }
);

const isDesktop = useMediaQuery("(min-width: 640px)");

const formattedKeybind = computed(() => {
  const hotkey = props.hotkey;
  return hotkey && formatKeybind(hotkey);
});

const ariaDescription = computed(() => {
  const description = props.description;
  const hotkey = formattedKeybind.value;

  if (description && hotkey) {
    return `${description} (${hotkey})`;
  } else if (description) {
    return description;
  } else if (hotkey) {
    return `Hotkey: ${hotkey}`;
  }

  return undefined;
});

const emphasisId = computed(() => {
  return props.label.replace(/\s+/g, "-").toLowerCase();
});

const cls = computed(() => {
  const variant = props.variant;
  const disabled = props.disabled;
  const active = props.active;
  const badge = props.badge;

  return normalizeClass([
    `relative m-0 flex items-center gap-2 rounded-full text-sm font-medium outline-2 outline-blue-500 transition focus-visible:outline`,
    props.icon ? `p-[0.4375rem] sm:px-2 sm:py-1` : `px-2 py-1`,

    variant === "primary" &&
      `border border-transparent bg-blue-500 text-white outline-offset-2 hover:bg-blue-700 active:bg-blue-700`,

    variant === "secondary" &&
      normalizeClass([
        `border border-transparent text-gray-700`,
        active
          ? `bg-gray-100 dark:bg-card dark:text-foreground`
          : badge
          ? `bg-yellow-500/15 hover:bg-yellow-500/30 active:bg-yellow-500/30 data-[state=open]:bg-yellow-500/30 dark:text-muted-foreground`
          : `hover:bg-gray-200 active:bg-gray-200 data-[state=open]:bg-gray-200 dark:text-muted-foreground dark:hover:bg-card dark:active:bg-card dark:data-[state=open]:bg-card`,
      ]),

    variant === "negative" &&
      `text-gray-700 hover:bg-red-600 hover:text-white active:bg-red-600 active:text-white dark:text-muted-foreground`,

    disabled && `cursor-not-allowed opacity-50`,
  ]);
});

const trigger = useTemplateRef("trigger");

useKeybinds(() => {
  const hotkey = props.hotkey;
  if (!hotkey || props.disabled) {
    return;
  }

  return {
    [hotkey]: {
      name: props.description ?? props.label,
      group: `Page`,
      run(ev) {
        if (ev.defaultPrevented) {
          return;
        }

        if (props.to) {
          router.push(props.to);
        } else if (props.href) {
          window.open(props.href, "_blank");
        } else {
          trigger.value?.click();
        }

        return true;
      },
    },
  };
});
</script>
